import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Row,
  Modal,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Pagination,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHistory,
  faEye,
  faCalendarAlt,
  faUser,
  faFileAlt,
  faMoneyBillWave,
  faSearch,
  faFilter,
  faSync,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faTimesCircle,
  faPlay,
  faUserCheck,
  faGavel,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { fetchApi } from "../../../redux/api";
import { formatNumber } from "../../../redux/utils";
import CustomTable from "../../../components/CustomTable";
import moment from "moment";
import { Routes } from "../../../routes";
import { useNavigate } from "react-router-dom";

// Enhanced status information with user-friendly descriptions and icons for releases
const getStatusInfo = (status) => {
  const statusLower = status?.toLowerCase() || "";
  
  const statusMap = {
    'initiated': {
      title: 'Release Initiated',
      description: 'Release has been initiated and is ready for processing',
      icon: faPlay,
      type: 'info'
    },
    'pending': {
      title: 'Pending Review',
      description: 'Awaiting review and verification by the next approver',
      icon: faClock,
      type: 'warning'
    },
    'reviewed': {
      title: 'Under Review',
      description: 'Release is being reviewed by authorized personnel',
      icon: faEye,
      type: 'info'
    },
    'approved': {
      title: 'Approved',
      description: 'Release has been approved and is ready for final processing',
      icon: faCheckCircle,
      type: 'success'
    },
    'scheduled': {
      title: 'Scheduled for Payment',
      description: 'Release has been scheduled for payment processing',
      icon: faCalendarAlt,
      type: 'info'
    },
    'paid': {
      title: 'Payment Completed',
      description: 'Release payment has been successfully processed and completed',
      icon: faHandshake,
      type: 'success'
    },
    'processing': {
      title: 'Payment Processing',
      description: 'Payment is currently being processed through the banking system',
      icon: faClock,
      type: 'warning'
    },
    'returned': {
      title: 'Returned for Correction',
      description: 'Release has been returned for corrections or additional information',
      icon: faExclamationTriangle,
      type: 'warning'
    },
    'rejected': {
      title: 'Rejected',
      description: 'Release has been rejected and will not be processed',
      icon: faTimesCircle,
      type: 'danger'
    },
    'requested': {
      title: 'Cash Backing Requested',
      description: 'Cash backing has been requested for this release',
      icon: faMoneyBillWave,
      type: 'info'
    }
  };

  // Try to find exact match first
  if (statusMap[statusLower]) {
    return statusMap[statusLower];
  }

  // Try partial matches for complex statuses
  for (const [key, value] of Object.entries(statusMap)) {
    if (statusLower.includes(key)) {
      return value;
    }
  }

  // Default fallback
  return {
    title: status || 'Unknown Status',
    description: 'Status information is being processed',
    icon: faFileAlt,
    type: 'secondary'
  };
};

// Generate progress steps based on status history for releases
const getProgressSteps = (statusHistory) => {
  const commonSteps = [
    { key: 'initiated', label: 'Initiated', icon: faPlay },
    { key: 'pending', label: 'Pending', icon: faClock },
    { key: 'reviewed', label: 'Reviewed', icon: faEye },
    { key: 'approved', label: 'Approved', icon: faCheckCircle },
    { key: 'scheduled', label: 'Scheduled', icon: faCalendarAlt },
    { key: 'processing', label: 'Processing', icon: faClock },
    { key: 'paid', label: 'Completed', icon: faHandshake }
  ];

  const historyStatuses = statusHistory.map(h => h.status.toLowerCase());
  const currentStatus = historyStatuses[historyStatuses.length - 1];

  return commonSteps.map(step => {
    if (historyStatuses.includes(step.key)) {
      return { ...step, status: step.key === currentStatus ? 'current' : 'completed' };
    } else if (step.key === 'initiated') {
      return { ...step, status: 'completed' }; // Always show initiated as completed
    } else {
      return { ...step, status: 'pending' };
    }
  });
};

function AllApprovalHistory() {
  const history = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [approvalList, setApprovalList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Filtering and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [mdaFilter, setMdaFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const getAllApprovalHistory = useCallback(() => {
    setLoading(true);
    
    const params = new URLSearchParams({
      page: currentPage,
      limit: 50,
      userRole: user?.role || 'USER',
      userMdaCode: user?.mda_code || '',
      userId: user?.username || '',
      ...(searchTerm && { search: searchTerm }),
      ...(statusFilter && { status: statusFilter }),
      ...(mdaFilter && { mda_name: mdaFilter }),
    });
    
    fetchApi(`get_all_approval_history?${params.toString()}`)
      .then((data) => {
        if (data.success) {
          setApprovalList(data.result || []);
          setPagination(data.pagination || {});
        }
      })
      .catch((err) => {
        console.error("Error fetching approval history:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentPage, searchTerm, statusFilter, mdaFilter, user?.role, user?.mda_code, user?.username]);

  useEffect(() => {
    getAllApprovalHistory();
  }, [getAllApprovalHistory]);

  const handleSearch = () => {
    setCurrentPage(1);
    getAllApprovalHistory();
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setMdaFilter("");
    setCurrentPage(1);
  };

  const handleViewHistory = (approval) => {
    setSelectedApproval(approval);
    setShowHistoryModal(true);
  };

  const getStatusBadgeVariant = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("approved") || statusLower.includes("paid") || statusLower.includes("completed")) {
      return "success";
    } else if (statusLower.includes("pending") || statusLower.includes("review") || statusLower.includes("processing")) {
      return "warning";
    } else if (statusLower.includes("returned") || statusLower.includes("rejected")) {
      return "danger";
    } else if (statusLower.includes("scheduled") || statusLower.includes("initiated")) {
      return "info";
    }
    return "primary";
  };

  // Role-based title and description
  const getRoleBasedTitle = () => {
    switch (user?.role) {
      case 'ADMIN':
        return {
          title: "Release Tracking (Admin View)",
          description: "Track all Release Collections across all MDAs and their status history"
        };
      case 'MDA ADMIN':
        return {
          title: `Release Tracking (${user?.mda_name || 'MDA'} View)`,
          description: `Track Release Collections for ${user?.mda_name || 'your MDA'} and their status history`
        };
      case 'USER':
        return {
          title: "My Release Activity History",
          description: "Track Release Collections you have worked on and their status history"
        };
      default:
        return {
          title: "Release Tracking",
          description: "Track Release Collections and their status history"
        };
    }
  };

  const roleInfo = getRoleBasedTitle();

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    // Page numbers
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={pagination.totalPages}
          onClick={() => handlePageChange(pagination.totalPages)}
        >
          {pagination.totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {((currentPage - 1) * pagination.limit) + 1} to{" "}
          {Math.min(currentPage * pagination.limit, pagination.total)} of{" "}
          {pagination.total} entries
        </div>
        <Pagination>
          <Pagination.Prev
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          />
          {items}
          <Pagination.Next
            disabled={currentPage === pagination.totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          />
        </Pagination>
      </div>
    );
  };

  const fields = [
    {
      title: "Approval No.",
      value: "approval_no",
    },
    {
      title: "MDA Name",
      value: "mda_name",
    },
    ...(user?.role === 'ADMIN' ? [{
      title: "MDA Code",
      value: "mda_code",
    }] : []),
    {
      title: "Approved By",
      value: "approved_by",
    },
    {
      title: "Amount (N)",
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.amount)}</div>
      ),
    },
    {
      title: "Current Status",
      custom: true,
      component: (item) => (
        <div color={getStatusBadgeVariant(item.current_status)}>
          {item.current_status}
        </div>
      ),
    },
    {
      title: "Approval Date",
      custom: true,
      component: (item) => (
        <div>{moment(item.approval_date).format("DD/MM/YYYY")}</div>
      ),
    },
    {
      title: "Actions",
      custom: true,
      component: (item) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleViewHistory(item)}
          >
            <FontAwesomeIcon icon={faHistory} className="me-1" />
            History
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => {
              history(
                `${Routes.approval.viewApproval}?approval_no=${encodeURIComponent(item.approval_no)}&approval_type=${encodeURIComponent(item.approval_type)}`
              );
            }}
          >
            <FontAwesomeIcon icon={faEye} className="me-1" />
            View Release
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            {roleInfo.title}
          </h4>
          <p className="mb-0">
            {roleInfo.description} - Total: {pagination.total} Releases
          </p>
          {user?.role === 'MDA ADMIN' && (
            <small className="text-muted">
              <FontAwesomeIcon icon={faFilter} className="me-1" />
              Filtered by MDA Code: {user?.mda_code}
            </small>
          )}
          {user?.role === 'USER' && (
            <small className="text-muted">
              <FontAwesomeIcon icon={faUser} className="me-1" />
              Showing only releases you have acted upon
            </small>
          )}
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <ButtonGroup>
            <Button variant="outline-primary" size="sm" onClick={getAllApprovalHistory}>
              <FontAwesomeIcon icon={faSync} className="me-1" />
              Refresh
            </Button>
            <Button variant="outline-primary" size="sm">
              Export
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {/* Search and Filter Section */}
      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Search Releases</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search by Approval No, MDA Name, or Approved By..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button variant="outline-primary" onClick={handleSearch}>
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="Initiated">Initiated</option>
                  <option value="Pending">Pending</option>
                  <option value="Reviewed">Reviewed</option>
                  <option value="Approved">Approved</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Processing">Processing</option>
                  <option value="Paid">Paid</option>
                  <option value="Returned">Returned</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Filter by MDA</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter MDA name..."
                  value={mdaFilter}
                  onChange={(e) => setMdaFilter(e.target.value)}
                  disabled={user?.role === 'MDA ADMIN'}
                />
                {user?.role === 'MDA ADMIN' && (
                  <Form.Text className="text-muted">
                    Restricted to your MDA: {user?.mda_name}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                onClick={handleClearFilters}
                className="w-100"
              >
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="p-0" style={{ height: "70vh", overflow: "scroll" }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : approvalList.length > 0 ? (
            <CustomTable fields={fields} data={approvalList} />
          ) : (
            <div className="text-center p-5">
              <FontAwesomeIcon icon={faFileAlt} size="3x" className="text-muted mb-3" />
              <h5 className="text-muted">No Releases Found</h5>
              <p className="text-muted">
                {searchTerm || statusFilter || mdaFilter
                  ? "No releases match your current filters. Try adjusting your search criteria."
                  : user?.role === 'USER'
                  ? "You haven't worked on any Release Collections yet, or no history is recorded."
                  : user?.role === 'MDA ADMIN'
                  ? `No Release Collections found for ${user?.mda_name || 'your MDA'}.`
                  : "No Release Collections are available in the system."}
              </p>
            </div>
          )}
        </Card.Body>
        
        {/* Pagination */}
        {renderPagination()}
      </Card>

      {/* Enhanced Status History Modal */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="xl">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="d-flex align-items-center">
            <div className="me-3">
              <FontAwesomeIcon icon={faHistory} className="me-2 text-primary" />
              Release Collection Journey
            </div>
            <Badge variant="outline-primary" className="fs-6">
              {selectedApproval?.approval_no}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApproval && (
            <>
              {/* Enhanced Release Summary */}
              <Card className="mb-4 border-0 shadow-sm">
                <Card.Header className="bg-gradient-primary text-white">
                  <h6 className="mb-0 fw-bold">
                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                    Release Collection Summary
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="g-4">
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">Approval Number</div>
                        <div className="info-value text-primary fw-bold">{selectedApproval.approval_no}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">Approval Type</div>
                        <div className="info-value">{selectedApproval.approval_type}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">Current Status</div>
                        <Badge color={getStatusBadgeVariant(selectedApproval.current_status)} className="status-badge">
                          {selectedApproval.current_status}
                        </Badge>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">MDA</div>
                        <div className="info-value">{selectedApproval.mda_name}</div>
                        {selectedApproval.mda_code && (
                          <div className="info-subtext">Code: {selectedApproval.mda_code}</div>
                        )}
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">Approved By</div>
                        <div className="info-value">{selectedApproval.approved_by}</div>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="info-item">
                        <div className="info-label">Amount</div>
                        <div className="info-value text-success fw-bold">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                          ₦{formatNumber(selectedApproval.amount)}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Progress Indicator */}
              {selectedApproval.status_history && selectedApproval.status_history.length > 0 && (
                <Card className="mb-4 border-0 shadow-sm">
                  <Card.Body className="p-4">
                    <h6 className="mb-3 fw-bold text-dark">
                      <FontAwesomeIcon icon={faClock} className="me-2 text-info" />
                      Processing Progress
                    </h6>
                    <div className="progress-steps">
                      {getProgressSteps(selectedApproval.status_history).map((step, index) => (
                        <div key={index} className={`progress-step ${step.status}`}>
                          <div className="step-icon">
                            <FontAwesomeIcon icon={step.icon} />
                          </div>
                          <div className="step-label">{step.label}</div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Enhanced Status History Timeline */}
              <h6 className="mb-4">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                Release Collection Journey
              </h6>
              
              {selectedApproval.status_history && selectedApproval.status_history.length > 0 ? (
                <div className="enhanced-timeline">
                  {selectedApproval.status_history.map((history, index) => {
                    const isLatest = index === selectedApproval.status_history.length - 1;
                    const statusInfo = getStatusInfo(history.status);
                    
                    return (
                      <div key={index} className={`timeline-item ${isLatest ? 'current' : 'completed'}`}>
                        <div className="timeline-marker">
                          <div className={`timeline-icon ${statusInfo.type}`}>
                            <FontAwesomeIcon icon={statusInfo.icon} />
                          </div>
                        </div>
                        
                        <div className="timeline-content">
                          <Card className={`timeline-card ${statusInfo.type}`}>
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h6 className="mb-1 fw-bold text-dark">
                                    {statusInfo.title}
                                  </h6>
                                  <p className="mb-1 text-muted small">
                                    {statusInfo.description}
                                  </p>
                                  <div className="text-muted small">
                                    <strong>Stage:</strong> {history.stage}
                                  </div>
                                </div>
                                <Badge variant={getStatusBadgeVariant(history.status)} className="status-badge">
                                  {history.status}
                                </Badge>
                              </div>
                              
                              <div className="timeline-meta">
                                <Row className="g-2">
                                  <Col md={6}>
                                    <div className="d-flex align-items-center text-muted small">
                                      <FontAwesomeIcon icon={faUser} className="me-2" />
                                      <div>
                                        <div className="fw-semibold">{history.updated_by_name || 'System'}</div>
                                      </div>
                                    </div>
                                  </Col>
                                  <Col md={6} className="text-end">
                                    <div className="text-muted small">
                                      <div className="fw-semibold">
                                        {moment(history.updated_at).format("DD MMM YYYY")}
                                      </div>
                                      <div>
                                        {moment(history.updated_at).format("HH:mm")} • {moment(history.updated_at).fromNow()}
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                              
                              {history.remarks && (
                                <div className="timeline-remarks mt-3 p-2 bg-light rounded">
                                  <div className="d-flex align-items-start">
                                    <FontAwesomeIcon icon={faFileAlt} className="me-2 mt-1 text-muted" />
                                    <div>
                                      <strong className="small text-dark">Remarks:</strong>
                                      <div className="text-muted small mt-1">{history.remarks}</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center p-5">
                  <div className="empty-state">
                    <FontAwesomeIcon icon={faHistory} size="3x" className="text-muted mb-3" />
                    <h6 className="text-muted mb-2">No Journey History</h6>
                    <p className="text-muted small">
                      This Release Collection doesn't have any recorded status changes yet.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="text-muted small">
              <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
              Last updated: {selectedApproval && moment(selectedApproval.status_history?.[selectedApproval.status_history.length - 1]?.updated_at).format("DD MMM YYYY, HH:mm")}
            </div>
            <div>
              <Button variant="outline-secondary" onClick={() => setShowHistoryModal(false)} className="me-2">
                <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                Close
              </Button>
              {selectedApproval && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowHistoryModal(false);
                    history(
                      `${Routes.approval.viewApproval}?approval_no=${encodeURIComponent(selectedApproval.approval_no)}&approval_type=${encodeURIComponent(selectedApproval.approval_type)}`
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faEye} className="me-1" />
                  View Full Release
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <style>{`
        /* Enhanced Timeline Styles */
        .enhanced-timeline {
          position: relative;
          padding-left: 0;
        }
        
        /* Enhanced Release Summary Styles */
        .bg-gradient-primary {
          background: linear-gradient(135deg, #007bff, #0056b3) !important;
        }
        
        .info-item {
          text-align: left;
        }
        
        .info-label {
          font-size: 0.875rem;
          color: #6c757d;
          font-weight: 500;
          margin-bottom: 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .info-value {
          font-size: 1rem;
          color: #212529;
          font-weight: 600;
          margin-bottom: 0.125rem;
        }
        
        .info-subtext {
          font-size: 0.75rem;
          color: #6c757d;
          font-style: italic;
        }
        
        /* Progress Steps Styles */
        .progress-steps {
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
          margin: 1rem 0;
        }
        
        .progress-steps::before {
          content: '';
          position: absolute;
          top: 20px;
          left: 0;
          right: 0;
          height: 2px;
          background: #e9ecef;
          z-index: 1;
        }
        
        .progress-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          flex: 1;
        }
        
        .step-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          margin-bottom: 0.5rem;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .progress-step.completed .step-icon {
          background: #28a745;
          color: white;
        }
        
        .progress-step.current .step-icon {
          background: #007bff;
          color: white;
          animation: pulse 2s infinite;
        }
        
        .progress-step.pending .step-icon {
          background: #f8f9fa;
          color: #6c757d;
          border-color: #dee2e6;
        }
        
        .step-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-align: center;
          color: #6c757d;
          max-width: 80px;
          line-height: 1.2;
        }
        
        .progress-step.completed .step-label {
          color: #28a745;
        }
        
        .progress-step.current .step-label {
          color: #007bff;
        }
        
        .timeline-item {
          position: relative;
          display: flex;
          margin-bottom: 2rem;
          min-height: 120px;
        }
        
        .timeline-item:last-child {
          margin-bottom: 0;
        }
        
        .timeline-marker {
          position: relative;
          flex-shrink: 0;
          width: 60px;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding-top: 1rem;
        }
        
        .timeline-item:not(:last-child) .timeline-marker::after {
          content: '';
          position: absolute;
          top: 50px;
          left: 50%;
          transform: translateX(-50%);
          width: 2px;
          height: calc(100% + 2rem);
          background: linear-gradient(to bottom, #007bff, #e9ecef);
          z-index: 1;
        }
        
        .timeline-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 16px;
          position: relative;
          z-index: 2;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          border: 3px solid white;
        }
        
        .timeline-icon.success {
          background: linear-gradient(135deg, #28a745, #20c997);
        }
        
        .timeline-icon.info {
          background: linear-gradient(135deg, #17a2b8, #007bff);
        }
        
        .timeline-icon.warning {
          background: linear-gradient(135deg, #ffc107, #fd7e14);
        }
        
        .timeline-icon.danger {
          background: linear-gradient(135deg, #dc3545, #e83e8c);
        }
        
        .timeline-icon.secondary {
          background: linear-gradient(135deg, #6c757d, #495057);
        }
        
        .timeline-content {
          flex: 1;
          margin-left: 1rem;
        }
        
        .timeline-card {
          border: none;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
          border-radius: 12px;
          overflow: hidden;
        }
        
        .timeline-card:hover {
          box-shadow: 0 4px 20px rgba(0,0,0,0.12);
          transform: translateY(-2px);
        }
        
        .timeline-card.success {
          border-left: 4px solid #28a745;
        }
        
        .timeline-card.info {
          border-left: 4px solid #17a2b8;
        }
        
        .timeline-card.warning {
          border-left: 4px solid #ffc107;
        }
        
        .timeline-card.danger {
          border-left: 4px solid #dc3545;
        }
        
        .timeline-card.secondary {
          border-left: 4px solid #6c757d;
        }
        
        .timeline-item.current .timeline-card {
          background: linear-gradient(135deg, #f8f9fa, #ffffff);
          border-left-width: 6px;
        }
        
        .timeline-item.current .timeline-icon {
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0% {
            box-shadow: 0 2px 8px rgba(0,0,0,0.15), 0 0 0 0 rgba(0, 123, 255, 0.7);
          }
          70% {
            box-shadow: 0 2px 8px rgba(0,0,0,0.15), 0 0 0 10px rgba(0, 123, 255, 0);
          }
          100% {
            box-shadow: 0 2px 8px rgba(0,0,0,0.15), 0 0 0 0 rgba(0, 123, 255, 0);
          }
        }
        
        .timeline-meta {
          border-top: 1px solid #f1f3f4;
          padding-top: 0.75rem;
          margin-top: 0.75rem;
        }
        
        .timeline-remarks {
          border: 1px solid #e9ecef;
          background: #f8f9fa;
        }
        
        .status-badge {
          font-size: 0.75rem;
          padding: 0.375rem 0.75rem;
          border-radius: 20px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .empty-state {
          padding: 2rem;
          border: 2px dashed #dee2e6;
          border-radius: 12px;
          background: #f8f9fa;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .timeline-marker {
            width: 50px;
          }
          
          .timeline-icon {
            width: 35px;
            height: 35px;
            font-size: 14px;
          }
          
          .timeline-content {
            margin-left: 0.75rem;
          }
          
          .timeline-meta .row {
            flex-direction: column;
          }
          
          .timeline-meta .col-md-6:last-child {
            text-align: left !important;
            margin-top: 0.5rem;
          }
          
          .progress-steps {
            flex-wrap: wrap;
            gap: 1rem;
          }
          
          .progress-step {
            flex: 0 0 calc(33.333% - 1rem);
            margin-bottom: 1rem;
          }
          
          .progress-steps::before {
            display: none;
          }
          
          .step-icon {
            width: 35px;
            height: 35px;
            font-size: 14px;
          }
          
          .step-label {
            font-size: 0.7rem;
            max-width: 70px;
          }
        }
      `}</style>
    </>
  );
}

export default AllApprovalHistory;